import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { PencilIcon } from "lucide-react";
import { BidDecisionGroup, MarkOrganizationAsNotSubmittingInput } from "@/graphql/__generated__/graphql";
import useMarkOrganizationsAsNotSubmittingMutationHook from "@/hooks/mutations/useMarkOrganizationsAsNotSubmittingMutation";
import { Form } from "@/components/ui/form";
import { createStepperFormSchema, type StepperFormData } from "./formSchema";
import { StepIndicator } from "./StepIndicator";
import { SelectionStepComponent } from "./SelectionStepComponent";
import { ReasonStepComponent } from "./ReasonStepComponent";
import { ConfirmationStepComponent } from "./ConfirmationStepComponent";
import { StepComponentProps } from "./types";
import { CircleLoader } from "@/components/shared/loaders";

/**
 * UpdateParticipantsStepperFormDialog component provides a multi-step form dialog
 * for updating participating organizations with a better UX flow.
 *
 * This is a wrapper component that handles:
 * - Dialog state management
 * - Form state management
 * - Step navigation
 * - Submission logic
 *
 * Steps:
 * 1. Select organizations to remove
 * 2. Enter shared reason and optional individual reasons
 * 3. Confirm changes with accordion view of reasons
 */
function UpdateParticipantsStepperFormDialog({ participatingGroups }: { participatingGroups: BidDecisionGroup[] }) {
  const { t } = useTranslation(["translation", "common", "modals"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { markOrganizationsAsNotSubmittingMutation, loading } = useMarkOrganizationsAsNotSubmittingMutationHook();

  const form = useForm<StepperFormData>({
    resolver: zodResolver(createStepperFormSchema(t)),
    defaultValues: {
      organizations: [],
      sharedReason: "",
    },
  });

  const { watch, handleSubmit } = form;
  const organizations = watch("organizations");
  const sharedReason = watch("sharedReason");

  // Get selected organizations based on organizations array
  const selectedOrganizations = useMemo(
    () =>
      participatingGroups.filter((org) =>
        organizations.some((formOrg) => formOrg.organizationId === org.operationalOrganizationId)
      ),
    [participatingGroups, organizations]
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsOpen(false);
      setCurrentStep(1);
      form.reset();
    } else {
      setIsOpen(true);
    }
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep === 1) {
      // Validate step 1
      if (organizations.length === 0) {
        form.setError("organizations", {
          message: t("modals:updateparticipantsstepperform.validation.atleastone"),
        });
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate step 2
      // Check if all organizations have individual reasons
      const allHaveIndividualReasons = organizations.every(
        (org) => org.reason && org.reason.trim().length > 0
      );

      // If not all have individual reasons, sharedReason is required
      if (!allHaveIndividualReasons) {
        if (!sharedReason || sharedReason.trim().length === 0) {
          form.setError("sharedReason", {
            message: t("modals:updateparticipantsstepperform.validation.reasonrequired"),
          });
          return;
        }
      }

      // Assign shared reason to organizations that don't have one
      const updatedOrganizations = organizations.map((org) => ({
        ...org,
        reason:
          org.reason && org.reason.trim().length > 0
            ? org.reason.trim()
            : (sharedReason || "").trim(),
      }));

      form.setValue("organizations", updatedOrganizations);
      setCurrentStep(3);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFormSubmit = (data: StepperFormData) => {
    if (data.organizations.length === 0) {
      return;
    }

    const formattedOrganizations: MarkOrganizationAsNotSubmittingInput[] = data.organizations.map(
      (org) => ({
        organizationId: org.organizationId,
        reason: org.reason.trim(),
      })
    );

    markOrganizationsAsNotSubmittingMutation({
      variables: {
        tenderId: Number(tenderID),
        organizations: formattedOrganizations,
      },
    }).then(() => {
      setIsOpen(false);
      setCurrentStep(1);
      form.reset();
    });
  };

  const stepProps: StepComponentProps = {
    form,
    participatingGroups,
    selectedOrganizations,
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <PencilIcon className="w-4 h-4 mr-1" /> {t("common:buttons.edit")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("modals:updateparticipantsstepperform.title")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} />

            {/* Step Components */}
            {currentStep === 1 && <SelectionStepComponent {...stepProps} />}
            {currentStep === 2 && <ReasonStepComponent {...stepProps} />}
            {currentStep === 3 && <ConfirmationStepComponent {...stepProps} />}

            {/* Navigation Buttons */}
            <DialogFooter className="flex justify-between sm:justify-between">
              <div>
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={handleBack}>
                    {t("common:buttons.back")}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                {currentStep < 3 ? (
                  <Button type="button" onClick={handleNext}>
                    {t("common:buttons.next")}
                  </Button>
                ) : (
                  <Button type="submit" variant="destructive" disabled={loading}>
                    <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                    {t("modals:confirmorganizationchanges.buttons.confirmchanges")}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default UpdateParticipantsStepperFormDialog;
